"use client";

import { useParams, usePathname, useSelectedLayoutSegments } from "next/navigation";
import { BrandingEntityType } from "database";
import type { BrandingLevel } from "@/lib/branding/types";

export type BrandingRouteInfo = {
  level: BrandingLevel;
  entityType: BrandingEntityType | "DEFAULT";
  entityId: string | null;
  domain: string | null;
  /** Whether a network fetch is appropriate for this route. */
  shouldFetch: boolean;
};

/**
 * True only for organization *detail* routes:
 * - /organization/[id|slug]/...
 * - /organizers/[organizerId]/...
 *
 * NOT the org list under a white-label:
 * - /white-label/[id]/organization  → white-label branding
 * - /admin/organization             → white-label branding
 */
function resolveOrganizationEntityId(
  pathname: string,
  segments: string[],
  organizerId: string | null
): string | null {
  if (organizerId) return organizerId;

  const orgSegmentIndex = segments.findIndex((s) => s === "organization");
  if (orgSegmentIndex >= 0) {
    const next = segments[orgSegmentIndex + 1];
    // Next segment must be an entity id/slug, not a nested list-only path
    if (next && next !== "create") return next;
  }

  // Fallback for pathnames where segments omit the id (e.g. parallel routes)
  const match = pathname.match(/\/organization\/([^/]+)/);
  if (match?.[1] && match[1] !== "create") return match[1];

  return null;
}

/**
 * Infer branding resolution level from the current route.
 * Priority: Event → Organization (detail only) → White-label → Default
 */
export function useBrandingRoute(): BrandingRouteInfo {
  const pathname = usePathname() ?? "";
  const params = useParams() as {
    domain?: string;
    id?: string;
    slug?: string;
    organizerId?: string;
  };
  const segments = useSelectedLayoutSegments() ?? [];

  const domain = params.domain ? decodeURIComponent(params.domain) : null;
  const id = params.id ?? null;
  const slug = params.slug ?? null;
  const organizerId = params.organizerId ?? null;

  const isAdminPath = pathname.includes("/admin");
  const isPublicEvent =
    pathname.includes("/event/") && !pathname.includes("/admin/");

  // Event: public /event/[slug] or admin .../event/[id]
  const isEventSegment =
    segments.includes("event") || pathname.includes("/event/");
  if (isPublicEvent && slug) {
    return {
      level: "EVENT",
      entityType: BrandingEntityType.EVENT,
      entityId: slug,
      domain,
      shouldFetch: true,
    };
  }
  if (isEventSegment && id && !pathname.includes("/organization/")) {
    return {
      level: "EVENT",
      entityType: BrandingEntityType.EVENT,
      entityId: id,
      domain,
      shouldFetch: true,
    };
  }

  // Organization detail only — list pages keep white-label branding
  const organizationEntityId = resolveOrganizationEntityId(
    pathname,
    segments,
    organizerId
  );
  if (organizationEntityId) {
    return {
      level: "ORGANIZATION",
      entityType: BrandingEntityType.ORGANIZATION,
      entityId: organizationEntityId,
      domain,
      shouldFetch: true,
    };
  }

  // White-label: domain routes or /white-label/[id]
  const isWhiteLabelSegment =
    segments.includes("white-label") || pathname.includes("/white-label/");
  if (isWhiteLabelSegment && id) {
    return {
      level: "WHITE_LABEL",
      entityType: BrandingEntityType.WHITE_LABEL,
      entityId: id,
      domain,
      shouldFetch: true,
    };
  }
  if (domain) {
    return {
      level: "WHITE_LABEL",
      entityType: BrandingEntityType.WHITE_LABEL,
      entityId: domain,
      domain,
      shouldFetch: true,
    };
  }

  // Platform admin without domain — no branding entity
  if (isAdminPath) {
    return {
      level: "DEFAULT",
      entityType: "DEFAULT",
      entityId: null,
      domain: null,
      shouldFetch: false,
    };
  }

  return {
    level: "DEFAULT",
    entityType: "DEFAULT",
    entityId: null,
    domain: null,
    shouldFetch: false,
  };
}
